import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Shield, CheckCircle, Clock, AlertTriangle, Lock, FileText } from 'lucide-react';
import Footer from '../components/Footer';

export default function BackgroundCheckPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft size={16} className="mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-2">
            <Shield className="text-blue-600" size={24} />
            <h1 className="text-xl font-heading font-bold">Background Check Policy</h1>
          </div>
        </div>
      </header>

      <main className="flex-1 container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Hero Section */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl p-8 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Shield size={40} />
              <h1 className="text-3xl font-heading font-bold">Background Check Policy</h1>
            </div>
            <p className="text-blue-100 text-lg">
              Our commitment to safety, transparency, and fair hiring practices.
            </p>
            <p className="text-sm text-blue-200 mt-4">
              Last Updated: January 2025
            </p>
          </div>

          {/* Content Sections */}
          <div className="space-y-8">
            {/* Overview */}
            <section className="bg-white rounded-lg p-6 shadow-sm">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <FileText className="text-blue-600" size={20} />
                Overview
              </h2>
              <p className="text-gray-600 leading-relaxed">
                The Interview partners with Checkr, a leading consumer reporting agency, to provide 
                background verification services. Background checks are an optional feature that allows 
                candidates to verify their credentials and stand out to potential employers. This policy 
                outlines how we collect, process, and protect your information during the background 
                check process.
              </p>
            </section>

            {/* What We Check */}
            <section className="bg-white rounded-lg p-6 shadow-sm">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <CheckCircle className="text-green-600" size={20} />
                What We Verify
              </h2>
              <p className="text-gray-600 mb-4">
                Depending on the package you select, our background checks may include:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium mb-2">Basic Package</h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• SSN Verification</li>
                    <li>• National Criminal Search</li>
                    <li>• Sex Offender Registry Check</li>
                  </ul>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium mb-2">Standard Package</h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• All Basic Package items</li>
                    <li>• County Criminal Search</li>
                    <li>• Employment Verification</li>
                  </ul>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg md:col-span-2">
                  <h3 className="font-medium mb-2">Premium Package</h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• All Standard Package items</li>
                    <li>• Education Verification</li>
                    <li>• Professional License Verification</li>
                    <li>• Credit Check (where legally permitted)</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Process */}
            <section className="bg-white rounded-lg p-6 shadow-sm">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Clock className="text-orange-600" size={20} />
                The Background Check Process
              </h2>
              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold flex-shrink-0">1</div>
                  <div>
                    <h3 className="font-medium">Select a Package & Pay</h3>
                    <p className="text-sm text-gray-600">Choose the background check package that meets your needs and complete the secure payment.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold flex-shrink-0">2</div>
                  <div>
                    <h3 className="font-medium">Provide Your Information</h3>
                    <p className="text-sm text-gray-600">Complete the information form with your full legal name, email, and current state of residence.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold flex-shrink-0">3</div>
                  <div>
                    <h3 className="font-medium">Consent & Authorization</h3>
                    <p className="text-sm text-gray-600">You will receive an email from Checkr to provide consent and any additional information required.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold flex-shrink-0">4</div>
                  <div>
                    <h3 className="font-medium">Processing</h3>
                    <p className="text-sm text-gray-600">Our team processes your request through Checkr. Most checks are completed within 2-5 business days.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center font-bold flex-shrink-0">5</div>
                  <div>
                    <h3 className="font-medium">Results & Verification Badge</h3>
                    <p className="text-sm text-gray-600">Once complete, you&apos;ll receive a notification and your profile will display a verified badge visible to employers.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Your Rights */}
            <section className="bg-white rounded-lg p-6 shadow-sm">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Lock className="text-purple-600" size={20} />
                Your Rights Under the FCRA
              </h2>
              <p className="text-gray-600 mb-4">
                The Fair Credit Reporting Act (FCRA) provides you with important rights regarding background checks:
              </p>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={16} />
                  <span><strong>Right to Consent:</strong> You must provide written consent before a background check is conducted.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={16} />
                  <span><strong>Right to Disclosure:</strong> You have the right to know if information in your report was used against you.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={16} />
                  <span><strong>Right to a Free Copy:</strong> You can request a free copy of your report within 60 days of an adverse action.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={16} />
                  <span><strong>Right to Dispute:</strong> You can dispute inaccurate or incomplete information in your report.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={16} />
                  <span><strong>Right to Privacy:</strong> Your information can only be provided to parties with a valid legal purpose.</span>
                </li>
              </ul>
            </section>

            {/* Data Protection */}
            <section className="bg-white rounded-lg p-6 shadow-sm">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Shield className="text-blue-600" size={20} />
                Data Protection & Security
              </h2>
              <p className="text-gray-600 mb-4">
                We take the security of your personal information seriously:
              </p>
              <ul className="space-y-2 text-gray-600">
                <li>• All data is encrypted in transit and at rest using industry-standard encryption</li>
                <li>• Access to background check information is strictly limited to authorized personnel</li>
                <li>• We do not sell or share your personal information with third parties for marketing purposes</li>
                <li>• Background check reports are retained only as long as necessary and in compliance with applicable laws</li>
                <li>• You can request deletion of your data at any time by contacting our support team</li>
              </ul>
            </section>

            {/* Important Notice */}
            <section className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-yellow-800">
                <AlertTriangle className="text-yellow-600" size={20} />
                Important Notice
              </h2>
              <div className="text-yellow-800 space-y-3">
                <p>
                  <strong>Voluntary Service:</strong> Background checks on The Interview are completely voluntary. 
                  Having a background check is not required to use our platform or apply for jobs.
                </p>
                <p>
                  <strong>Not a Guarantee:</strong> A completed background check does not guarantee employment 
                  or that all information is accurate. Employers make their own hiring decisions.
                </p>
                <p>
                  <strong>State Restrictions:</strong> Some background check components may not be available 
                  in all states due to local laws and regulations.
                </p>
              </div>
            </section>

            {/* Contact */}
            <section className="bg-white rounded-lg p-6 shadow-sm">
              <h2 className="text-xl font-semibold mb-4">Questions or Concerns?</h2>
              <p className="text-gray-600 mb-4">
                If you have any questions about our background check policy or need to dispute information 
                in your report, please contact us:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-medium">The Interview Support Team</p>
                <p className="text-gray-600">Email: support@theinterviewpros.com</p>
                <p className="text-gray-600">Response Time: Within 2 business days</p>
              </div>
              <p className="text-sm text-gray-500 mt-4">
                For disputes directly with Checkr, visit{' '}
                <a href="https://checkr.com/dispute" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  checkr.com/dispute
                </a>
              </p>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
